import { forwardRef, isValidElement, useMemo } from 'react';
import flattenChildren from 'react-keyed-flatten-children';

import type { GridItemProps } from '../extensions';
import { gridItem } from '../extensions';
import styledFactory from '../styledFactory';
import variantFactory from '../variantFactory';
import type { IListContext, IListContextNotFirstChild } from './ListContext';
import ListContext from './ListContext';
import ListItem from './ListItem';

export interface SystemListProps extends GridItemProps {
  __listCommon__?: 'common';
  __listComponentCommon__?: 'common';
}

type Target = 'ul' | 'ol';

const createListComponent = (
  target: Target,
  displayName: string,
  variantKey: string,
) => {
  const Component = styledFactory<SystemListProps, Target>(
    target,
    displayName,
    [
      variantFactory('components.list', { prop: '__listCommon__' }),
      variantFactory(`components.list.${variantKey}`, {
        prop: '__listComponentCommon__',
      }),
      variantFactory(`components.list.${variantKey}.variants`),
    ],
    [gridItem],
  );

  return Component;
};

const SystemList = createListComponent('ul', 'UnorderedList', 'unordered');

const SystemBulletList = createListComponent('ul', 'BulletList', 'bulleted');

const SystemOrderedList = createListComponent('ol', 'NumberList', 'numbered');

export type SharedProps = {
  axis?: 'vertical' | 'horizontal';
  gap?: any; // TODO something like this? ResponsiveValue<ThemeValue<'space', DefaultTheme>> | undefined;
} & React.ComponentPropsWithRef<ReturnType<typeof createListComponent>>;

export type ListProps = {
  divided?: boolean;
  dividedVariant?: string;
  variant?: string;
} & SharedProps;

export type BulletListProps = { bulletVariant?: string } & SharedProps;

export type NumberListProps = { numberVariant?: string } & SharedProps;

export type ListComponent =
  | typeof SystemList
  | typeof SystemOrderedList
  | typeof SystemBulletList;

export type BaseListProps = {
  ListComponent: ListComponent;
  __bulletCommon__?: 'common';
  __bulletVariant__?: string;
  __numberCommon__?: 'common';
  __numberVariant__?: string;
} & ListProps;

const BaseList = forwardRef<HTMLUListElement | HTMLOListElement, BaseListProps>(
  (
    {
      axis = 'vertical',
      gap,
      variant = 'default',
      divided = false,
      dividedVariant = 'default',
      ListComponent,
      children,
      __bulletCommon__,
      __bulletVariant__,
      __numberCommon__,
      __numberVariant__,
      ...listProps
    },
    ref,
  ) => {
    const listItemProps: IListContext = useMemo(() => {
      const itemProps: IListContext = {
        __bulletCommon__,
        __bulletVariant__,
        __numberCommon__,
        __numberVariant__,
      };

      if (axis === 'horizontal') {
        itemProps.display = 'inline-block';
      }

      return itemProps;
    }, [
      __bulletCommon__,
      __bulletVariant__,
      __numberCommon__,
      __numberVariant__,
      axis,
    ]);

    const listItemPropsNotFirstChild: IListContextNotFirstChild =
      useMemo(() => {
        const itemProps: IListContextNotFirstChild = { ...listItemProps };

        const marginKey = axis === 'vertical' ? 'mt' : 'ml';
        const paddingKey = axis === 'vertical' ? 'pt' : 'pl';

        if (gap != null) {
          itemProps[marginKey] = gap;
        }

        if (divided) {
          const dividedVariantCommonKey =
            axis === 'vertical'
              ? '__dividedVerticalCommon__'
              : '__dividedHorizontalCommon__';
          const dividedVariantKey =
            axis === 'vertical'
              ? '__dividedVertical__'
              : '__dividedHorizontal__';
          itemProps[dividedVariantCommonKey] = 'common';
          itemProps[dividedVariantKey] = dividedVariant;
        }

        if (gap != null && divided) {
          itemProps[paddingKey] = gap;
        }

        return itemProps;
      }, [axis, listItemProps, divided, dividedVariant, gap]);

    return (
      <ListContext.Provider value={listItemProps}>
        <ListComponent
          ref={ref}
          __listCommon__="common"
          __listComponentCommon__="common"
          variant={variant}
          {...listProps}
        >
          {flattenChildren(children).map((child, idx) => {
            if (isValidElement(child) && idx > 0 && (divided || gap != null)) {
              return (
                <ListContext.Provider
                  key={child.key}
                  value={listItemPropsNotFirstChild}
                >
                  {child}
                </ListContext.Provider>
              );
            }

            return child;
          })}
        </ListComponent>
      </ListContext.Provider>
    );
  },
);

/**
 * List is a component that renders an unordered list. By default a list is laid out vertically
 * but you can change this with the axis prop. With the gap prop you can specify the space
 * between the list items and with the divided prop a divider line is added between the items.
 *
 * List expects to be rendered with List.listItem children.
 */
const List = forwardRef<HTMLUListElement, ListProps>(
  (
    { axis, gap, divided = false, dividedVariant = 'default', ...props },
    ref,
  ) => (
    <BaseList
      ref={ref}
      axis={axis}
      gap={gap}
      ListComponent={SystemList}
      divided={divided}
      dividedVariant={dividedVariant}
      {...props}
    />
  ),
);

/**
 * Bullet List is a specialized list component that renders items with a bullet symbol.
 * The style of the bullets are determined by the theme.
 */
const BulletList = forwardRef<HTMLUListElement, BulletListProps>(
  ({ axis, gap, bulletVariant = 'default', ...props }, ref) => {
    if ('divided' in props) {
      throw new Error('The `divided` prop is not supported on Bulletlist');
    }

    return (
      <BaseList
        ref={ref}
        axis={axis}
        gap={gap}
        ListComponent={SystemBulletList}
        __bulletCommon__="common"
        __bulletVariant__={bulletVariant}
        {...props}
      />
    );
  },
);

/**
 * Number List is a specialized list component that renders an ordered numbered list.
 * The style of the numbers are determined by the theme.
 */
const NumberList = forwardRef<HTMLOListElement, NumberListProps>(
  ({ axis, gap, numberVariant = 'default', ...props }, ref) => {
    if ('divided' in props) {
      throw new Error('The `divided` prop is not supported on NumberList');
    }

    return (
      <BaseList
        ref={ref}
        axis={axis}
        gap={gap}
        ListComponent={SystemOrderedList}
        __numberCommon__="common"
        __numberVariant__={numberVariant}
        {...props}
      />
    );
  },
);

export default Object.assign(List, {
  bulletList: BulletList,
  numberList: NumberList,
  listItem: ListItem,
});
